import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useRef } from 'react';

import gestopAPI, {
    gestopApiForExternalEndpoints,
    refreshAuthSession,
    registerAuthSessionHandlers,
    resetAuthSessionExpiry
} from 'utils/gestopAPI';
import { errorProcessor, getIriFromObj } from 'utils/processors';
import Loader from 'ui-component/Loader';
import useConfig from 'hooks/useConfig';
import authReducer, {
    initialAuthState,
    LOGIN,
    LOGOUT,
    UPDATE_USER,
    UPDATE_LICENSE,
    UPDATE_PROFILE_SETTINGS,
    DELETE_PROFILE_SETTINGS
} from 'contexts/auth/reducer';
import {
    ApiProfileSettingsPostCollection,
    ApiProfileSettingsDeleteCollection,
    ApiProfileSettingsPatchItem
} from 'store/app/admin/profile-setting';
import { buildConfigFromProfileSettings, clearAuthSession, loadAuthSession, saveAuthSession, verifyToken } from 'contexts/auth/session';

const hasUserData = (user = {}) => Object.keys(user || {}).length > 0;

const normalizeProfileSettingComparableValue = (value) => {
    if (Array.isArray(value)) {
        return JSON.stringify(value);
    }

    if (value && typeof value === 'object') {
        return JSON.stringify(value);
    }

    return value;
};

const hasSameProfileSettingValue = (currentValue, nextValue) =>
    normalizeProfileSettingComparableValue(currentValue) === normalizeProfileSettingComparableValue(nextValue);

const applyProfileSettingsToConfig = (updateConfig = () => {}, profileSettings = []) => {
    const userProfileConfig = buildConfigFromProfileSettings(profileSettings);

    if (Object.keys(userProfileConfig).length > 0) {
        updateConfig(userProfileConfig);
    }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const { updateConfig, resetTenantPresetColor } = useConfig();

    const [state, dispatch] = useReducer(authReducer, initialAuthState);

    // Tokens are read from storage because the API interceptors may have refreshed them after the last render.
    const persistSession = (sessionOverrides = {}) => {
        const storedSession = loadAuthSession();

        return saveAuthSession({
            serviceToken: storedSession?.serviceToken || state.serviceToken,
            refreshToken: storedSession?.refreshToken || state.refreshToken,
            keepMeLoggedIn: state.keepMeLoggedIn,
            user: state.user,
            profileSettings: state.profileSettings,
            license: state.license,
            ...sessionOverrides
        });
    };

    const dumpUser = () => {
        clearAuthSession();
        resetTenantPresetColor();
        dispatch({ type: LOGOUT });
    };

    // Keeps the latest callbacks available to the API session recovery handlers, which are registered once on mount.
    const authSessionCallbacksRef = useRef({});
    authSessionCallbacksRef.current = {
        onRefreshed: (session) => {
            // During initialisation the refreshed session is hydrated by init() itself.
            if (state.isInitialized && state.isLoggedIn) {
                dispatch({ type: LOGIN, payload: session });
            }
        },
        onExpired: () => {
            resetTenantPresetColor();
            dispatch({ type: LOGOUT });
        }
    };

    const hydrateSession = (session = {}) => {
        const normalizedSession = saveAuthSession(session);

        applyProfileSettingsToConfig(updateConfig, normalizedSession.profileSettings);
        dispatch({ type: LOGIN, payload: normalizedSession });

        return normalizedSession;
    };

    /**
     * Refreshes the session through the shared API refresh, so the whole app issues a single in-flight refresh request.
     * Returns the hydrated session, or null when it could not be refreshed. A missing or rejected refresh token always
     * clears the local session; a transient failure (network or server error) clears it only when
     * `dumpOnTransientFailure` is set.
     */
    const refreshToken = async (refreshTokenValue, sessionFallback = {}, { dumpOnTransientFailure = true } = {}) => {
        let refreshedSession = null;
        const storedSession = loadAuthSession();

        // The shared refresh reads the refresh token from storage; persist an explicitly provided one if it is missing.
        if (refreshTokenValue && !storedSession?.refreshToken) {
            saveAuthSession({ ...(storedSession || {}), refreshToken: refreshTokenValue });
        }

        try {
            refreshedSession = await refreshAuthSession();
        } catch (error) {
            if (error?.isAuthSessionExpired || dumpOnTransientFailure) {
                dumpUser();
            }

            return null;
        }

        return hydrateSession({
            ...refreshedSession,
            keepMeLoggedIn:
                typeof sessionFallback.keepMeLoggedIn === 'boolean' ? sessionFallback.keepMeLoggedIn : state.keepMeLoggedIn || true,
            license:
                Object.keys(refreshedSession.license || {}).length > 0 ? refreshedSession.license : sessionFallback.license || state.license
        });
    };

    const updateUser = (userData = {}) => {
        const nextUser = { ...(state.user || {}), ...userData };

        persistSession({ user: nextUser });
        dispatch({ type: UPDATE_USER, payload: { user: nextUser } });
    };

    const updateLicense = (newLicense = {}) => {
        persistSession({ license: newLicense || {} });
        dispatch({ type: UPDATE_LICENSE, payload: { license: newLicense || {} } });
    };

    const login = async (username, password, keepMeLoggedIn) => {
        const response = await gestopApiForExternalEndpoints.post('security/login', { username, password });
        const { token, refresh_token: refreshTokenValue, user, profileSettings, license } = response?.data || {};

        resetAuthSessionExpiry();
        hydrateSession({
            serviceToken: token || null,
            refreshToken: refreshTokenValue || null,
            keepMeLoggedIn: Boolean(keepMeLoggedIn),
            user: user || {},
            profileSettings: profileSettings || [],
            license: license || {}
        });
    };

    const register = async (username, email, password, name, lastName) => {
        await gestopApiForExternalEndpoints.post('security/register', {
            username,
            email,
            password,
            name,
            lastName
        });
    };

    // The local session is always cleared, even when the server rejects the token (401) or cannot be reached.
    const logout = async (successCallback = () => {}, errorCallback = () => {}) => {
        let response = null;
        let logoutError = null;

        try {
            response = await gestopAPI.get('security/logout');
        } catch (error) {
            logoutError = error;
        } finally {
            dumpUser();
        }

        if (logoutError) {
            if (typeof errorCallback === 'function') {
                errorCallback(errorProcessor(logoutError?.response?.data));
            }
        } else if (typeof successCallback === 'function') {
            successCallback(response);
        }
    };

    const requestLoginLink = async (usernameOrEmail = '', successCallback = () => {}, errorCallback = () => {}) => {
        await gestopApiForExternalEndpoints
            .post('security/requestloginlink', { usernameOrEmail })
            .then((response) => {
                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            })
            .catch((error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(errorProcessor(error?.response?.data));
                }
            });
    };

    const resetPassword = async (username, newPassword, successCallback = () => {}, errorCallback = () => {}) => {
        await gestopApiForExternalEndpoints
            .post('security/resetpassword', { username, newPassword, mode: newPassword ? 'withPassword' : 'withoutPassword' })
            .then((response) => {
                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            })
            .catch((error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(errorProcessor(error?.response?.data));
                }
            });
    };

    const updateProfileSetting = (settingName = '', settingData = {}, successCallback = () => {}, errorCallback = () => {}) => {
        const profileSettings = state.profileSettings || [];
        const profSettingIdx = profileSettings.findIndex((setting) => setting?.name === settingName);

        if (profSettingIdx === -1) {
            ApiProfileSettingsPostCollection({
                data: { user: getIriFromObj(state.user), name: settingName, ...settingData },
                successCallback: (response) => {
                    const nextProfileSettings = [...profileSettings, response];

                    persistSession({ profileSettings: nextProfileSettings });
                    dispatch({ type: UPDATE_PROFILE_SETTINGS, payload: { profileSettings: nextProfileSettings } });

                    if (typeof successCallback === 'function') {
                        successCallback();
                    }
                },
                errorCallback: (error) => {
                    if (typeof errorCallback === 'function') {
                        errorCallback(error);
                    }
                }
            });
        } else if (!hasSameProfileSettingValue(profileSettings.at(profSettingIdx)?.fieldValue, settingData?.fieldValue)) {
            ApiProfileSettingsPatchItem({
                id: profileSettings.at(profSettingIdx)?.id,
                data: settingData,
                successCallback: (response) => {
                    const nextProfileSettings = [...profileSettings];
                    nextProfileSettings.splice(profSettingIdx, 1, response);

                    persistSession({ profileSettings: nextProfileSettings });
                    dispatch({ type: UPDATE_PROFILE_SETTINGS, payload: { profileSettings: nextProfileSettings } });

                    if (typeof successCallback === 'function') {
                        successCallback();
                    }
                },
                errorCallback: (error) => {
                    if (typeof errorCallback === 'function') {
                        errorCallback(error);
                    }
                }
            });
        } else if (typeof successCallback === 'function') {
            successCallback();
        }
    };

    const deleteProfileSettings = (settingsIds = [], successCallback = () => {}, errorCallback = () => {}) => {
        if (settingsIds?.length === 0) return;

        ApiProfileSettingsDeleteCollection({
            ids: settingsIds,
            successCallback: () => {
                const nextProfileSettings = (state.profileSettings || []).filter(
                    (profileSetting) => !settingsIds.includes(profileSetting?.id)
                );

                persistSession({ profileSettings: nextProfileSettings });
                dispatch({ type: DELETE_PROFILE_SETTINGS, payload: { settingsIds } });

                if (typeof successCallback === 'function') {
                    successCallback();
                }
            },
            errorCallback: (error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(error);
                }
            }
        });
    };

    useEffect(
        () =>
            registerAuthSessionHandlers({
                onRefreshed: (session) => authSessionCallbacksRef.current.onRefreshed(session),
                onExpired: () => authSessionCallbacksRef.current.onExpired()
            }),
        []
    );

    useEffect(() => {
        let isMounted = true;

        const init = async () => {
            try {
                const storedSession = loadAuthSession();

                if (!storedSession) {
                    if (isMounted) {
                        dispatch({ type: LOGOUT });
                    }
                    return;
                }

                const hasValidToken = verifyToken(storedSession.serviceToken);

                if (hasValidToken && hasUserData(storedSession.user)) {
                    if (isMounted) {
                        hydrateSession(storedSession);
                    }
                    return;
                }

                if (storedSession.keepMeLoggedIn && storedSession.refreshToken) {
                    const refreshedSession = await refreshToken(storedSession.refreshToken, storedSession, {
                        dumpOnTransientFailure: false
                    });

                    if (!isMounted || refreshedSession) {
                        return;
                    }

                    // The refresh failed transiently (offline or server error): keep the stored session so the
                    // API interceptors can recover it once the server is reachable again.
                    const sessionAfterRefresh = loadAuthSession();

                    if (sessionAfterRefresh?.refreshToken && hasUserData(sessionAfterRefresh.user)) {
                        hydrateSession(sessionAfterRefresh);
                        return;
                    }
                }

                if (isMounted) {
                    dumpUser();
                }
            } catch (error) {
                if (isMounted) {
                    dumpUser();
                }
            }
        };

        init();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!state?.isInitialized) {
        return <Loader />;
    }

    const contextState = { ...state };
    delete contextState.serviceToken;
    delete contextState.refreshToken;

    return (
        <JWTContext.Provider
            value={{
                ...contextState,
                login,
                refreshToken,
                logout,
                register,
                requestLoginLink,
                resetPassword,
                updateUser,
                updateLicense,
                dumpUser,
                updateProfileSetting,
                deleteProfileSettings
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
